import { useAppContext } from '../../providers';
import { usePlants } from '../usePlants/usePlants';
import { useDieNumberLookup } from './useDieNumberLookup';
import { useLumberScheduleLookup } from './useLumberScheduleLookup';
import { useLumberPartLookup } from './useLumberPartLookup';
import { usePartDescription } from './usePartDescription';
import { usePartQohLocationsLookup } from './usePartQohLocationsLookup';
import { usePutDownMillLookup } from './usePutDownMillLookup';

const QUERY_IDS = {
  DIE_NUMBER_LOOKUP: 'DIE_NUMBER_LOOKUP',
  PART_QOH_LOCATIONS_LOOKUP: 'PART_QOH_LOCATIONS_LOOKUP',
  PART_DESCRIPTION_LOOKUP: 'PART_DESCRIPTION_LOOKUP',
  LUMBER_SCHEDULE_LOOKUP: 'LUMBER_SCHEDULE_LOOKUP',
  LUMBER_PART_LOOKUP: 'LUMBER_PART_LOOKUP',
  P10_PUT_DOWN_MILL: 'P10_PUT_DOWN_MILL',
};

/**
 * Run the selected query by calling all delegates unconditionally and returning
 * the result that matches the active queryType. Each delegate gates its own
 * execution via the `enabled` flag so only the active query fetches data.
 *
 * Returns a normalized shape: { data, isPending, error } where error is a
 * human-readable message string (or null). Each delegate is responsible for
 * mapping its own failure modes — including soft not-found cases — to a
 * single error message so this dispatcher stays query-type agnostic.
 * @param {{id: string, description?: string}} queryType
 * @param {string} input
 */
export const useActionQuery = ({ id }, input) => {
  const isEnabled = Object.values(QUERY_IDS).includes(id);
  const { selectedPlant } = useAppContext();
  const { data: plants } = usePlants();
  const currentPlant = plants?.find((p) => p.id === selectedPlant);
  const organizationId = currentPlant?.organizationId;

  // Call all hooks unconditionally (React requirement)
  // Each hook gates its own execution via the `enabled` flag
  const dieNumber = useDieNumberLookup({
    organizationId,
    item: input,
    enabled: id === QUERY_IDS.DIE_NUMBER_LOOKUP,
  });

  const partQohLocations = usePartQohLocationsLookup({
    organizationId,
    item: input,
    enabled: id === QUERY_IDS.PART_QOH_LOCATIONS_LOOKUP,
  });

  const partDescription = usePartDescription({
    input: { partNumber: input },
    enabled: id === QUERY_IDS.PART_DESCRIPTION_LOOKUP,
  });

  const lumberSchedule = useLumberScheduleLookup({
    scheduleNumber: input,
    enabled: id === QUERY_IDS.LUMBER_SCHEDULE_LOOKUP,
  });

  const lumberPart = useLumberPartLookup({
    item: input,
    enabled: id === QUERY_IDS.LUMBER_PART_LOOKUP,
  });

  const putDownMill = usePutDownMillLookup({
    item: input,
    enabled: id === QUERY_IDS.P10_PUT_DOWN_MILL,
  });

  // Query registry: map query IDs to their corresponding hook results
  // To add a new query type: (1) add the hook call above, (2) add entry here
  const queryRegistry = {
    [QUERY_IDS.DIE_NUMBER_LOOKUP]: dieNumber,
    [QUERY_IDS.PART_QOH_LOCATIONS_LOOKUP]: partQohLocations,
    [QUERY_IDS.PART_DESCRIPTION_LOOKUP]: partDescription,
    [QUERY_IDS.LUMBER_SCHEDULE_LOOKUP]: lumberSchedule,
    [QUERY_IDS.LUMBER_PART_LOOKUP]: lumberPart,
    [QUERY_IDS.P10_PUT_DOWN_MILL]: putDownMill,
  };

  // Look up the active query result (no nested ternaries needed)
  const activeQuery = queryRegistry[id];

  return {
    data: activeQuery?.data,
    isPending: isEnabled && activeQuery?.isPending,
    error: isEnabled ? activeQuery?.error : null,
  };
};
